import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retried && !original.url.includes("/auth/")) {
      original._retried = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return axios(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(r.data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(r.data);
        } catch {
          setUser(null);
        }
      }
    })();
  }, []);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(r.data);
  };

  const register = async (name, email, password) => {
    const r = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
    setUser(r.data);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const toggleWish = async (productId) => {
    const r = await axios.post(`${API}/auth/wishlist`, { product_id: productId }, { withCredentials: true });
    setUser((u) => (u ? { ...u, wishlist: r.data.wishlist } : u));
    return r.data.wishlist.includes(productId);
  };

  const toggleSeries = async (series) => {
    const r = await axios.post(`${API}/auth/series-alerts`, { series }, { withCredentials: true });
    setUser((u) => (u ? { ...u, series_alerts: r.data.series_alerts } : u));
    return r.data.subscribed;
  };

  const toggleStock = async (productId) => {
    const r = await axios.post(`${API}/auth/stock-alerts`, { product_id: productId }, { withCredentials: true });
    setUser((u) => (u ? { ...u, stock_alerts: r.data.stock_alerts } : u));
    return r.data.active;
  };

  const togglePull = async (series) => {
    const r = await axios.post(`${API}/auth/pull-list`, { series }, { withCredentials: true });
    setUser((u) => (u ? { ...u, pull_list: r.data.pull_list } : u));
    return r.data.active;
  };

  return <AuthContext.Provider value={{ user, login, register, logout, toggleWish, toggleSeries, toggleStock, togglePull }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
